import { useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { ApiKeysService, CreateApiKeyResponse } from "../../../services/apiKeysService";

interface CreateApiKeyFormProps {
  onApiKeyCreated: (response: CreateApiKeyResponse, keyName: string) => void;
  isLoading?: boolean;
}

export default function CreateApiKeyForm({ onApiKeyCreated, isLoading }: CreateApiKeyFormProps) {
  const [keyName, setKeyName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!keyName.trim()) {
      setError("Please enter a name for your API key");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await ApiKeysService.createApiKey(keyName.trim());
      onApiKeyCreated(response, keyName.trim());
      setKeyName("");
    } catch (err) {
      console.error("Failed to create API key:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to create API key. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyName(e.target.value);
    if (error) setError(null);
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h4 className="text-white mb-1">Create New API Key</h4>
          <p className="text-color mb-0">
            Generate a new API key for programmatic access to the Windpuls API.
            Choose a descriptive name to help you identify its purpose.
          </p>
        </Col>
      </Row>

      <Container
        className="p-4 background-color-light border-radius-4"
        style={{ border: '1px solid #283654' }}
      >
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="wind-form-label mb-2">
              API Key Name
            </Form.Label>
            <div className="d-flex gap-3">
              <Form.Control
                type="text"
                value={keyName}
                onChange={handleNameChange}
                placeholder="e.g., Production App, Development Server, Mobile App"
                className="wind-form-control flex-grow-1"
                style={{ height: '48px' }}
                maxLength={100}
                disabled={isSubmitting || isLoading}
                autoFocus
              />
              <Button
                type="submit"
                className="wind-btn"
                style={{ height: '48px', minWidth: '180px' }}
                disabled={!keyName.trim() || isSubmitting || isLoading}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <span className="me-2">🔑</span>
                    Create API Key
                  </>
                )}
              </Button>
            </div>
            <Form.Text className="text-color" style={{ fontSize: '12px' }}>
              Choose a name that helps you identify the purpose of this API key.
              Maximum 100 characters.
            </Form.Text>
          </Form.Group>

          {error && (
            <Container className="p-3 mb-3" style={{
              backgroundColor: '#83030320',
              border: '1px solid #83030350',
              borderRadius: '4px'
            }}>
              <div className="d-flex align-items-center">
                <div className="me-2" style={{ color: '#ff4444' }}>⚠️</div>
                <div className="text-danger" style={{ fontSize: '14px' }}>
                  {error}
                </div>
              </div>
            </Container>
          )}

          <Row className="mt-4">
            <Col>
              <Container className="p-3" style={{
                backgroundColor: '#0ba4bf10',
                border: '1px solid #0ba4bf30',
                borderRadius: '8px'
              }}>
                <div className="d-flex align-items-start">
                  <div className="me-3" style={{ color: '#0ba4bf', fontSize: '20px' }}>📋</div>
                  <div>
                    <div className="text-white mb-2" style={{ fontWeight: '600' }}>
                      What happens next?
                    </div>
                    <ol className="text-color mb-0" style={{ fontSize: '14px', paddingLeft: '20px', lineHeight: '1.5' }}>
                      <li className="mb-1">We'll generate a unique API key for you</li>
                      <li className="mb-1">You'll see the key in a secure popup modal</li>
                      <li className="mb-1">Copy the key immediately - it won't be shown again</li>
                      <li>Store it safely in your application configuration or environment variables</li>
                    </ol>
                  </div>
                </div>
              </Container>
            </Col>
          </Row>
        </Form>
      </Container>

      <Row className="mt-4">
        <Col>
          <Container className="p-3" style={{
            backgroundColor: '#83030310',
            border: '1px solid #83030330',
            borderRadius: '8px'
          }}>
            <div className="d-flex align-items-start">
              <div className="me-3" style={{ color: '#ff4444', fontSize: '20px' }}>🔒</div>
              <div>
                <div className="text-white mb-2" style={{ fontWeight: '600' }}>
                  Important Security Information
                </div>
                <ul className="text-color mb-0" style={{ fontSize: '14px', paddingLeft: '20px', lineHeight: '1.5' }}>
                  <li className="mb-1">API keys are sensitive credentials - treat them like passwords</li>
                  <li className="mb-1">Never share your API key publicly or commit it to version control</li>
                  <li className="mb-1">Use different keys for different environments (dev, staging, prod)</li>
                  <li>Rotate your keys regularly and delete compromised keys immediately</li>
                </ul>
              </div>
            </div>
          </Container>
        </Col>
      </Row>
    </Container>
  );
}